<template>
  <div class="bank-account" data-testid="bank-account-form">
    <div
      v-for="(bank, index) in bankAccounts"
      :key="index"
      class="bank-account__bank"
      data-testid="bank-account"
    >
      <div class="bank-account__header">
        <Heading :level="3">
          {{ getString('common_field_bankaccount_label', [index + 1]) }}
        </Heading>
        <BaseText v-if="bankAccounts.length > 1" size="sm">
          <Link
            data-testid="remove-bank-account"
            @click.prevent="removeBank(bank, index)"
          >
            {{ getString('common_field_bankaccount_button_remove') }}
            {{ getString('common_field_bankaccount_label', [index + 1]) }}
          </Link>
        </BaseText>
      </div>
      <hr />
      <div class="bank-account__icons mb">
        <label
          v-for="{ id, icon } in quickSelectableBanks"
          :key="id"
          data-testid="bank-options"
          :class="{ active: !bank.bank || id === bank.bank }"
          :for="`${index}-bank-account-${id}`"
          @click="bank.bank = id"
        >
          <input
            :id="`${index}-bank-account-${id}`"
            type="radio"
            :name="`bank-${index}`"
            :checked="isSelectedBank(id, bank.bank)"
            required
          />
          <Tooltip
            :text="bankCodes[id] || 'Outro'"
            class="bank-account__icons-tooltip"
          >
            <img slot="content" :src="icon" :alt="bankCodes[id] || 'Outro'" />
          </Tooltip>
        </label>
      </div>
      <div v-if="bank.bank" class="bank-account__body" data-testid="bank-info">
        <InputSelect
          id="bank"
          v-model="bank.bank"
          class="mb"
          :label="getString('common_field_bankaccount_bank_label')"
          :options="bankOptions"
          placeholder="Selecione o seu banco"
          name="bank"
          required
          :disabled="bank.bank !== 'selectBank'"
        />
        <CheckboxRadio
          v-if="!onlyCurrentType(bank)"
          v-model="bank.type"
          class="mb"
          :title="getString('common_field_bankaccount_type_label')"
          :name="`type-${index}`"
          :values="['current', 'savings']"
          :labels="[
            getString('deposit_bank_account_type_current'),
            getString('deposit_bank_account_type_savings')
          ]"
        />
        <div>
          <BankData
            class="mb"
            :bank="bank"
            @update-bank-data="data => handleBankData(data, index)"
          />
        </div>
        <CheckboxRadio
          v-model="bank.hasSecondHolder"
          class="mb"
          :title="getString('deposit_bank_label_account_owner')"
          :name="`firstHolder-${index}`"
          :values="[false, true]"
          :labels="[
            getString('common_field_bankaccount_first_holder'),
            getString('common_field_bankaccount_second_holder')
          ]"
        />
        <div v-if="bank.hasSecondHolder" class="user-account__row">
          <CPFInput
            v-model="bank.firstHolderCPF"
            :name="`firstHolderCPF-${index}`"
            data-testid="first-holder-cpf"
          />
          <InputLabelField
            v-model="bank.firstHolderName"
            :name="`firstHolderName-${index}`"
            :label="getString('common_field_bankaccount_fullname_label')"
            required
          />
        </div>
      </div>
    </div>
    <ButtonSecondary
      :label="getString('common_field_bankaccount_button_add')"
      data-testid="add-bank-account"
      @click.prevent="addNewBank()"
    />
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { IBankAccount } from '@/types/models';

import { CheckboxRadio } from '@/modules/account/views/UpdateUserAccount/components/CheckboxRadio';
import { CPFInput } from './components/CPFInput';
import { InputLabelField } from '@/modules/account/components/InputLabelField';
import {
  InputSelect,
  Link,
  BaseText,
  ButtonSecondary,
  Heading
} from '@warrenbrasil/nebraska-web';
import { Tooltip } from '@/modules/common/components/Tooltip';
import { BankData } from './components/BankData';

import { getString, getEnum } from '@/modules/common/helpers/resources';

type BankAccount = Partial<IBankAccount>;

function getBankTemplate(): BankAccount {
  return {
    bank: undefined,
    agency: '',
    number: '',
    digit: '',
    type: 'current',
    hasSecondHolder: false,
    firstHolderCPF: '',
    firstHolderName: '',
    _id: ''
  };
}

const otherBank = {
  id: 'selectBank',
  icon: require('@/assets/imgs/icons/ic_3dots_black.svg')
};

@Component({
  components: {
    CheckboxRadio,
    CPFInput,
    InputLabelField,
    InputSelect,
    Tooltip,
    BankData,
    Link,
    BaseText,
    ButtonSecondary,
    Heading
  }
})
export default class BankAccountForm extends Vue {
  @Prop({ default: () => [], required: false })
  private bankAccounts!: BankAccount[];

  private getString = getString;

  private bankCodes: { [key: string]: string } = getEnum('Bank');
  private quickSelectableBanks = [
    { id: '001', icon: require('@/assets/imgs/banks/bancodobrasil.svg') },
    { id: '260', icon: require('@/assets/imgs/banks/nubank.svg') },
    { id: '237', icon: require('@/assets/imgs/banks/bradesco.svg') },
    { id: '104', icon: require('@/assets/imgs/banks/caixa.svg') },
    { id: '341', icon: require('@/assets/imgs/banks/itau.svg') },
    { id: '077', icon: require('@/assets/imgs/banks/inter.svg') },
    { id: '033', icon: require('@/assets/imgs/banks/santander.svg') },
    otherBank
  ];

  private removedBanks: string[] = [];

  @Emit('validate-field')
  validateValues() {
    const isValuesValid = this.valuesValidation();
    return { field: 'bankData', isValid: isValuesValid };
  }

  private valuesValidation() {
    const verificated: boolean[] = [];
    this.bankAccounts.forEach(data => {
      verificated.push(data.agency !== '0000');
      verificated.push(data.number !== '');
      verificated.push(data.digit !== '');
    });

    return verificated.every(bool => bool === true);
  }

  private mounted() {
    if (!this.bankAccounts.length) this.addNewBank();
  }

  private isSelectedBank(id: string, selectedId?: string) {
    if (id !== otherBank.id) return selectedId === id;
    return !!selectedId;
  }

  private onlyCurrentType(bank: BankAccount) {
    const nubankCode = '260';
    const interCode = '077';
    const onlyHasCurrent = bank.bank === nubankCode || bank.bank === interCode;
    if (onlyHasCurrent) bank.type = 'current';
    return onlyHasCurrent;
  }

  private addNewBank() {
    this.bankAccounts.push(getBankTemplate());
  }

  private get bankOptions() {
    return Object.keys(this.bankCodes)
      .sort()
      .map(bankCode => ({
        value: bankCode,
        label: `${bankCode} - ${this.bankCodes[bankCode]}`
      }));
  }

  private removeBank(bank: BankAccount, index: number) {
    const id = bank._id;
    if (id) {
      this.removedBanks.push(id);
      this.$parent?.$emit('removeBanks', this.removedBanks);
    }
    this.bankAccounts.splice(index, 1);
  }

  private handleBankData({ agency, number, digit }: any, index: number) {
    this.bankAccounts[index].agency = agency;
    this.bankAccounts[index].number = number;
    this.bankAccounts[index].digit = digit;
    this.validateValues();
  }
}
</script>

<style lang="less" scoped>
.bank-account {
  &__bank {
    margin-bottom: @size-spacing-x600;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__icons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: @size-spacing-x300;
    justify-content: space-between;
    text-align: center;
    @media @tabletUp {
      grid-template-columns: repeat(8, 1fr);
    }
    > * {
      opacity: 1;
      cursor: pointer;
      transition: 0.3s;
      &:not(.active) {
        opacity: 0.1;
      }
      &:hover {
        opacity: 1;
        transform: translateY(-10px);
      }
      img {
        width: 56px;
        max-width: 100%;
      }
    }
  }
}
/deep/.bank-account__icons {
  label {
    position: relative;
  }
  input[type='radio'] {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &-tooltip {
    .content {
      width: auto;
      min-width: 120px;
    }
    .icon {
      display: none;
    }
  }
}
.mb {
  margin-bottom: @size-spacing-x500;
}
</style>
